import { Component, EventEmitter, Input, OnInit, Output, ViewChild } from '@angular/core';
import { UntypedFormControl, UntypedFormGroup, Validators } from '@angular/forms';

import { ModalComponent } from '@components/index';

import { ProcessType } from '@models/process-type.model';
import { PermissionService } from "@app/authentication/services/permission.service";
import {RiskClass} from "@app/strain/config/risk-classes/models/risk-class.model";
import {Log} from "ng2-logger";
import {TableConfig} from "@app/shared/util/table-config.utils";
import {ProcessTypeService} from "@services/process-type.service";
import { User } from '@models/user.model';
import { UserService } from '@services/user.service';

@Component({
    selector: 'app-process-type-create-modal',
    templateUrl: './process-type-create-modal.component.html',
    styleUrls: ['./process-type-create-modal.component.scss']
})
export class ProcessTypeCreateModalComponent implements OnInit {

    _logger = Log.create(ProcessTypeCreateModalComponent.name);

    @ViewChild(ModalComponent, {static: true}) modal: ModalComponent;

    @Output('onCreate')
    onCreate: EventEmitter<any> = new EventEmitter();
    @Output('onUpdate')
    onUpdate: EventEmitter<any> = new EventEmitter();
    @Output('onDelete')
    onDelete: EventEmitter<any> = new EventEmitter();

    @Input() isLoading;

    public processType: ProcessType = new ProcessType();

    processTypeForm: UntypedFormGroup;
    previousProcessTypeName = '';
    showValidations = false;

    riskClasses: RiskClass[] = [];
    users: User[] = [];

    constructor(private permissionService: PermissionService,
                private processTypeService: ProcessTypeService,
                private userService: UserService) {
    }

    ngOnInit() {
        this.onFormInit();

        if (this.shouldRenderModifiers()) {
            this.getRiskClasses();
            this.getUsers();
        }
    }

    open(editProcessType = new ProcessType()) {
        this.resetForm();
        this.loadFormValue(editProcessType)

        this.showValidations = false;

        this.modal.open();
    }

    close() {
        this.modal.close();
        this.resetForm();
    }

    loadFormValue(processType) {
        this.processType = Object.assign({}, processType);
        this.previousProcessTypeName = processType.name || '';

        this.processTypeForm.setValue({
            id: processType.id,
            name: processType.name,
            description: processType.description,
            isDestructive: processType.isDestructive ? processType.isDestructive : false,
            technicalEvaluationClassId: processType.technicalEvaluationClass?.id
                ? processType.technicalEvaluationClass
                : null,
            maximumStrainMeasurementValue: processType.maximumStrainMeasurementValue ? processType.maximumStrainMeasurementValue : 800,
            strainResponsible: processType.strainResponsible || null
        });
    }

    onFormInit() {
        this.processTypeForm = new UntypedFormGroup({
            id: new UntypedFormControl(null),
            name: new UntypedFormControl(this.processType.name, Validators.required),
            description: new UntypedFormControl(this.processType.description, [Validators.required]),
            isDestructive: new UntypedFormControl(false),
            technicalEvaluationClassId: new UntypedFormControl(null),
            maximumStrainMeasurementValue: new UntypedFormControl(null),
            strainResponsible: new UntypedFormControl(null)
        });
    }

    createProcessType() {
        if (this.processTypeForm.invalid) {
            this.showValidations = true;
            return;
        }

        const payload = {
            ...this.processTypeForm.value,
            technicalEvaluationClassId: this.processType.technicalEvaluationClass?.id || null,
            strainResponsible: this.processTypeForm.value.strainResponsible || null
        };

        this.onCreate.emit(payload);
    }

    updateProcessType() {
        if (this.processTypeForm.invalid) {
            this.showValidations = true;
            return;
        }

        const payload = {
            ...this.processTypeForm.value,
            technicalEvaluationClassId: this.processType.technicalEvaluationClass?.id || null,
            strainResponsible: this.processTypeForm.value.strainResponsible || null
        };
        this.onUpdate.emit({
            previousProcessTypeName: this.previousProcessTypeName,
            newProcessType: payload
        });
    }

    deleteProcessType() {
        this.onDelete.emit(this.processType);
    }


    shouldRenderModifiers() {
        return this.permissionService.hasHasPermissionForFeature('MANAGE_PROCESS_TYPES');
    }

    onTechnicalEvaluationClassChange(riskClass) {
        this.processType.technicalEvaluationClass = riskClass;
    }

    onStrainResponsibleChange(user: User) {
        this.processType.strainResponsible = user;
    }

    get isEditMode() {
        return !!this.processType.id;
    }

    private getRiskClasses() {
        const tableConfig = new TableConfig();
        tableConfig.pager.pageSize = -1;

        this.processTypeService.getAllRiskClasses(tableConfig).subscribe(
            (data: any) => {
                this._logger.info('getRiskClasses:: API returned an error', data);
                this.riskClasses = data.items;
            },
            (error: any) => {
                this._logger.error('getRiskClasses:: API returned an error', error);
            }
        );
    }

    private getUsers() {
        const tableConfig = new TableConfig();
        tableConfig.pager.pageSize = -1;
        this.userService.getUsers(tableConfig).subscribe(
            (data: any) => {
                this.users = data.users;
            },
            (error: any) => {
                this._logger.error('getUsers:: API returned an error', error);
            }
        );
    }

    private resetForm() {
        this.processType = new ProcessType();
        this.previousProcessTypeName = '';
        this.showValidations = false;
        if (this.processTypeForm) {
            this.processTypeForm.reset({
                id: null,
                name: null,
                description: null,
                isDestructive: false,
                technicalEvaluationClassId: null,
                maximumStrainMeasurementValue: null,
                strainResponsible: null
            });
        }
    }
}
